import {
  BETA_MODEL_VERSIONS,
  ENTERPRISE_COPY,
  isEnterpriseDistribution,
  MODEL_IMAGE_MAP,
  MODEL_VERSION,
} from '@/app/constants';
import type { FormTabType, ModelVersion, UserTagKey } from '@/app/types';
import { isImageTask } from './task-types';
import { isModelVersionAvailable } from './validate-settings';

const NEW_MODEL_VERSIONS = new Set<ModelVersion>([
  MODEL_VERSION.GPT_IMAGE_2_5_FAST,
  MODEL_VERSION.GPT_IMAGE_2_5_PRO,
  MODEL_VERSION.Q4,
  MODEL_VERSION.Q3_LITE,
]);

export type ModelItem = {
  value: ModelVersion;
  image: string;
  title: string;
  description: string;
  isNew?: boolean;
};

interface BuildModelItemsOptions {
  type: Exclude<FormTabType, 'scene'>;
  getModelVersionText: (modelVersion: ModelVersion) => string;
  getDescription: (key: string) => string;
  userTags: Record<UserTagKey, boolean>;
}

const getModelDescription = ({
  type,
  modelVersion,
  getDescription,
}: {
  type: Exclude<FormTabType, 'scene'>;
  modelVersion: ModelVersion;
  getDescription: (key: string) => string;
}) => {
  switch (modelVersion) {
    case MODEL_VERSION.GPT_IMAGE_2:
      return getDescription('create.createForm.settings.modelSelection.OptionDescriptionSpecialGptImage2');
    case MODEL_VERSION.GPT_IMAGE_2_5_FAST:
      return getDescription('create.createForm.settings.modelSelection.OptionDescriptionSpecialGptImage25Fast');
    case MODEL_VERSION.GPT_IMAGE_2_5_PRO:
      return getDescription('create.createForm.settings.modelSelection.OptionDescriptionSpecialGptImage25Pro');
    case MODEL_VERSION.Q3_LITE:
      return getDescription('create.createForm.settings.modelSelection.OptionDescriptionSpecialQ3Lite');
    case MODEL_VERSION.Q3_PRO:
      return getDescription('create.createForm.settings.modelSelection.OptionDescriptionSpecialQ3Pro');
    case MODEL_VERSION.Q3_FAST:
      return getDescription('create.createForm.settings.modelSelection.OptionDescriptionSpecialQ3Fast');
    case MODEL_VERSION.Q3_A:
      return isEnterpriseDistribution
        ? ENTERPRISE_COPY.model.q3aDescription
        : getDescription('create.createForm.settings.modelSelection.OptionDescriptionSpecialQ3A');
    case MODEL_VERSION.Q3_HH:
      return isEnterpriseDistribution ? ENTERPRISE_COPY.model.q3bDescription : '';
    case MODEL_VERSION.Q4:
      return getDescription('create.createForm.settings.modelSelection.OptionDescription3_4');
    case MODEL_VERSION.Q3:
      return getDescription('create.createForm.settings.modelSelection.OptionDescription3_2');
    case MODEL_VERSION.Q2_PRO:
      return getDescription('create.createForm.settings.modelSelection.OptionDescription3_1_pro');
    case MODEL_VERSION.Q2:
      return isImageTask({ type })
        ? getDescription('create.createForm.settings.modelSelection.OptionDescription3_1_image')
        : getDescription('create.createForm.settings.modelSelection.OptionDescription3_1');
    case MODEL_VERSION.Q1:
      return isImageTask({ type })
        ? getDescription('create.createForm.settings.modelSelection.OptionDescription3_0_image')
        : getDescription('create.createForm.settings.modelSelection.OptionDescription3_0');
    case MODEL_VERSION['2.0']:
      return getDescription('create.createForm.settings.modelSelection.OptionDescription2_0');
  }
};

export const buildModelItems = ({
  type,
  getModelVersionText,
  getDescription,
  userTags,
}: BuildModelItemsOptions): ModelItem[] => {
  const isBetaAccessible = (version: ModelVersion): boolean => {
    const requiredTag = BETA_MODEL_VERSIONS[type]?.[version];
    return !requiredTag || userTags[requiredTag];
  };

  const items: ModelItem[] = [
    MODEL_VERSION.GPT_IMAGE_2_5_FAST,
    MODEL_VERSION.GPT_IMAGE_2_5_PRO,
    MODEL_VERSION.GPT_IMAGE_2,
    MODEL_VERSION.Q3_LITE,
    MODEL_VERSION.Q3_PRO,
    MODEL_VERSION.Q3_FAST,
    MODEL_VERSION.Q3_A,
    MODEL_VERSION.Q3_HH,
    MODEL_VERSION.Q4,
    MODEL_VERSION.Q3,
    MODEL_VERSION.Q2_PRO,
    MODEL_VERSION.Q2,
    MODEL_VERSION.Q1,
    MODEL_VERSION['2.0'],
  ].map((modelVersion) => ({
    value: modelVersion,
    image: MODEL_IMAGE_MAP[modelVersion],
    title: getModelVersionText(modelVersion),
    description: getModelDescription({ type, modelVersion, getDescription }),
    ...(NEW_MODEL_VERSIONS.has(modelVersion) ? { isNew: true } : {}),
  }));

  return items.filter((item) => isModelVersionAvailable(type, item.value) && isBetaAccessible(item.value));
};
